import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { registerUser, wakeUpServer } from '../actions/auth';
import { TextField, Button, Typography, Box, useTheme, Alert, InputAdornment, IconButton, CircularProgress } from '@mui/material';
import { Link } from 'react-router-dom';
import { useAlert } from '../contexts/AlertContext';
import { Visibility, VisibilityOff } from '@mui/icons-material';

const Register = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [isFirstRequest, setIsFirstRequest] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [serverWokenUp, setServerWokenUp] = useState(false);
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const theme = useTheme();

  useEffect(() => {
    const wakeServer = async () => {
      const success = await wakeUpServer();
      setServerWokenUp(success);
    };
    wakeServer();
  }, []);

  // Password validation function (same as Settings page)
  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    
    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }
    
    return errors;
  };

  // Check if password meets all requirements
  const isPasswordValid = (password) => {
    return validatePassword(password).length === 0;
  };

  // Handle password field blur
  const handlePasswordBlur = () => {
    if (password && !isPasswordValid(password)) {
      setShowPasswordRequirements(true);
    } else {
      setShowPasswordRequirements(false);
    }
  };

  // Handle password field focus
  const handlePasswordFocus = () => {
    setShowPasswordRequirements(false);
  };

  // Get password requirements message
  const getPasswordRequirementsMessage = () => {
    const requirements = [];
    
    if (password.length < 8) {
      requirements.push('at least 8 characters long');
    }
    if (!/[A-Z]/.test(password)) {
      requirements.push('one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      requirements.push('one lowercase letter');
    }
    if (!/\d/.test(password)) {
      requirements.push('one number');
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      requirements.push('one special character');
    }
    
    return requirements.join(', ');
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validate passwords match
    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }
    
    // Validate password requirements
    const passwordErrors = validatePassword(password);
    if (passwordErrors.length > 0) {
      setPasswordError(`Password requirements not met: ${passwordErrors.join(', ')}`);
      return;
    }
    
    setPasswordError('');

    setIsLoading(true);
    if (isFirstRequest && !serverWokenUp) {
      showAlert('Initial request may take a few seconds to load. Please be patient.', 'info');
      setIsFirstRequest(false);
    }

    try {
      await registerUser(name, email, password, confirmPassword);
      showAlert('Registration successful! Please login.', 'success');
      navigate('/login');
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Registration failed. Please try again.';
      showAlert(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStoreDownload = (storeUrl) => {
    window.open(storeUrl, '_blank');
  };

  return (
    <Box sx={{ 
      minHeight: '85vh',
      display: 'flex',
      flexDirection: 'column',
      background: theme.palette.mode === 'dark' ? '#121212' : '#ffffff',
      position: 'relative'
    }}>
      {/* Main Content */}
      <Box sx={{ 
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3
      }}>
        <Box sx={{ 
          maxWidth: 450,
          width: '100%',
          background: theme.palette.mode === 'dark' 
            ? 'rgba(255, 255, 255, 0.05)'
            : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          borderRadius: 4,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.3)'
            : '0 8px 32px rgba(0, 0, 0, 0.1)',
          border: theme.palette.mode === 'dark'
            ? '1px solid rgba(255, 255, 255, 0.1)'
            : '1px solid rgba(255, 255, 255, 0.2)',
          p: 4
        }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography 
              variant="h3" 
              gutterBottom 
              sx={{ 
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 1
              }}
            >
              Create Account
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary"
              sx={{ fontSize: '1.1rem' }}
            >
              Join us and start planning your events
            </Typography>
          </Box>

          {/* Register Form */}
          <form onSubmit={handleRegister}>
            <TextField
              label="Name"
              fullWidth
              margin="normal"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              disabled={isLoading}
              tabIndex={1}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.05)' 
                    : 'rgba(0, 0, 0, 0.02)',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.08)' 
                      : 'rgba(0, 0, 0, 0.04)',
                  },
                  '&.Mui-focused': {
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.1)' 
                      : 'rgba(0, 0, 0, 0.06)',
                  }
                }
              }}
            />
            <TextField
              label="Email"
              type='email'
              fullWidth
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
              tabIndex={2}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.05)' 
                    : 'rgba(0, 0, 0, 0.02)',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.08)' 
                      : 'rgba(0, 0, 0, 0.04)',
                  },
                  '&.Mui-focused': {
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.1)' 
                      : 'rgba(0, 0, 0, 0.06)',
                  }
                }
              }}
            />
            <TextField
              label="Password"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              margin="normal"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={handlePasswordBlur}
              onFocus={handlePasswordFocus}
              required
              tabIndex={3}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      tabIndex={-1}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.05)' 
                    : 'rgba(0, 0, 0, 0.02)',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.08)' 
                      : 'rgba(0, 0, 0, 0.04)',
                  },
                  '&.Mui-focused': {
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.1)' 
                      : 'rgba(0, 0, 0, 0.06)',
                  }
                }
              }}
            />

            {/* Password Requirements Alert */}
            {showPasswordRequirements && (
              <Alert severity="warning" sx={{ mt: 1, mb: 2 }}>
                Password must contain: {getPasswordRequirementsMessage()}
              </Alert>
            )}

            <TextField
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              fullWidth
              margin="normal"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={!!passwordError}
              helperText={passwordError}
              required
              tabIndex={4}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.05)' 
                    : 'rgba(0, 0, 0, 0.02)',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.08)' 
                      : 'rgba(0, 0, 0, 0.04)',
                  },
                  '&.Mui-focused': {
                    backgroundColor: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.1)' 
                      : 'rgba(0, 0, 0, 0.06)',
                  }
                }
              }}
            />
            <Button 
              type="submit" 
              variant="contained" 
              color="primary" 
              sx={{ 
                mt: 3, 
                mb: 2,
                py: 1.5,
                borderRadius: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                '&:hover': {
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease'
              }} 
              fullWidth
              disabled={isLoading || !name || !email || !password || !confirmPassword || !isPasswordValid(password)}
              tabIndex={5}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Create Account'}
            </Button>
            
            <Typography sx={{ mt: 3, textAlign: 'center' }}>
              Already have an account?{' '}
              <Link 
                to="/login" 
                style={{ 
                  color: theme.palette.primary.main,
                  textDecoration: 'none',
                  fontWeight: 600,
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                Sign in here
              </Link>
            </Typography>
          </form>
        </Box>
      </Box>

      {/* Footer with Links */}
      <Box sx={{ 
        p: 3,
        textAlign: 'center'
      }}>
        {/* Store Download Badges */}
        <Box sx={{ 
          display: 'flex', 
          gap: 3, 
          justifyContent: 'center', 
          flexWrap: 'wrap', 
          alignItems: 'center', 
          mb: 3 
        }}>
          {/* Apple App Store Badge */}
          <Box 
            component="img"
            src={theme.palette.mode === 'dark' 
              ? "/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg"
              : "/Download_on_the_App_Store_Badge_US-UK_RGB_wht_092917.svg"
            }
            alt="Download on the App Store"
            onClick={() => handleStoreDownload('https://apps.apple.com/us/app/event-plannnig-app/id6747792111')}
            sx={{ 
              height: 50, 
              cursor: 'pointer',
              transition: 'transform 0.2s ease-in-out',
              '&:hover': {
                opacity: 0.8,
                transform: 'scale(1.05)'
              }
            }}
          />
          
          {/* Microsoft Store Badge */}
          <Box
            component="a"
            href="https://apps.microsoft.com/detail/9NP2G8Q29V1K?referrer=appbadge&mode=direct"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ 
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none'
            }}
          >
            <Box
              component="img"
              src={theme.palette.mode === 'dark' 
                ? "https://get.microsoft.com/images/en-us%20dark.svg"
                : "https://get.microsoft.com/images/en-us%20light.svg"
              }
              alt="Get it from Microsoft"
              sx={{ 
                height: 50,
                cursor: 'pointer',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  opacity: 0.8,
                  transform: 'scale(1.05)'
                }
              }}
            />
          </Box>
        </Box>
        
        {/* Footer Links */}
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'center',
          gap: 3
        }}>
          <Link 
            to="/support" 
            style={{ 
              color: theme.palette.text.secondary, 
              textDecoration: 'none',
              fontSize: '0.9rem',
              fontWeight: 500,
              transition: 'color 0.2s ease-in-out',
            }}
            onMouseEnter={(e) => e.target.style.color = theme.palette.primary.main}
            onMouseLeave={(e) => e.target.style.color = theme.palette.text.secondary}
          >
            Support
          </Link>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: '0.9rem' }}
          >
            |
          </Typography>
          <Link 
            to="/privacy" 
            style={{ 
              color: theme.palette.text.secondary, 
              textDecoration: 'none',
              fontSize: '0.9rem',
              fontWeight: 500,
              transition: 'color 0.2s ease-in-out',
            }}
            onMouseEnter={(e) => e.target.style.color = theme.palette.primary.main}
            onMouseLeave={(e) => e.target.style.color = theme.palette.text.secondary}
          >
            Privacy Policy
          </Link>
        </Box>
      </Box>
    </Box>
  );
};

export default Register;
